import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { Observable, Subject } from 'rxjs';

/**
 * Holds the dynamic panel's half of "applying a search lands you on All Results": it watches the panel's
 * own search and records that its folder menu should move.
 *
 * Its reason for existing is a mount-ordering problem. The folder menu (`DPContextMenuDirective`) lives in
 * the panel body, which the panel unmounts whenever a form is open in it — and a knot or tag chip on such
 * a form is one of the ways a search starts in here. A menu subscribing to `newSearchApplied()` itself
 * would therefore not exist at the moment the search it needs to react to is published, and on remount
 * that search is already its baseline, so it would never land at all.
 *
 * Provided by `DynamicPanelComponent`, which spans the form and the list and sits inside the `'dp'` space
 * context — that placement is what makes this see the panel's own `AdvancedSearchService` rather than the
 * main view's. See devdoc/specs/search-and-folders.md.
 */
@Injectable()
export class DpSearchLandingService {
  private readonly searchService = inject(AdvancedSearchService);
  private readonly landing = new Subject<void>();
  private pending = false;

  constructor() {
    this.searchService
      .newSearchApplied()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.pending = true;
        this.landing.next();
      });
  }

  /** Fires for a search applied while a folder menu is mounted. */
  public onLanding(): Observable<void> {
    return this.landing.asObservable();
  }

  /**
   * Whether a search has been applied that no folder menu has acted on yet, clearing the record as it
   * reports it. Read on mount, to collect a search published while the menu was unmounted.
   */
  public consumePending(): boolean {
    const pending = this.pending;
    this.pending = false;

    return pending;
  }

  /**
   * Discards the record without acting on it. Called by the live {@link onLanding} consumer, which has the
   * search first-hand and only needs the record not to survive and relocate the next mount.
   */
  public clearPending(): void {
    this.pending = false;
  }
}
